import React from 'react';
import { useListPresentationContextData } from '../../context/ListPresentationContext';
import { BLOG_SECTION_MAX_WIDTH, getPaddingForInstaWidget, MAX_SECTION_WIDTH, onCarouselScroll } from '../../utils';
import classNames from 'classnames';
import { COMMON_LAYOUT_TYPE } from '../Testimonials/Constants';
import { useWidgetDndContextData } from 'src/context/WidgetDndContext';
import { ADMIN_WIDGETS } from 'src/constants/widgets';

export function LayoutWrapper({ children }) {
  const data = useListPresentationContextData();
  const props = data.props;
  const { cardFrameConfig, listPresentationConfig, isMobile, contentTypeConditions } =
    data.state;
  const { sectionContainerRefSetter } = data.refSetters;
  const { getConfigsAndUtils } = data.utilities;
  const {
    shouldAutoScrollSlider,
    isSliderFullWidth,
    containerHorizontalPadding,
    sectionColors,
  } = getConfigsAndUtils().generalValues;

  const {
    widgetContextState: { activeWidgetId, isBuilder },
    widgetContextHelpers: { handleWidgetPropChange },
  } = useWidgetDndContextData();

  const containerVerticalPadding =
    listPresentationConfig?.[!isMobile ? 'paddingY' : 'mobilePaddingY'] || 0;
  const containerTopMargin = listPresentationConfig?.marginTop;
  const containerBottomMargin = listPresentationConfig?.marginBottom;

  function handleHeadingClick() {
    if (
      (contentTypeConditions.multiColumn ||
        contentTypeConditions.logoSlider ||
        contentTypeConditions.testimonials ||
        contentTypeConditions.gallery) &&
      isBuilder
    ) {
      handleWidgetPropChange(
        {
          subWidget: '',
          subWidgetIndex: -1,
        },
        activeWidgetId
      );
    }
  }

  function getSectionStyles(): React.CSSProperties {
    return {
      padding: props?.isBlogListingPage
        ? 0
        : `${containerVerticalPadding}px ${isSliderFullWidth ? 0 : containerHorizontalPadding}px`,
      margin: `${containerTopMargin}px ${isSliderFullWidth ? '' : 'auto'} ${containerBottomMargin}px ${isSliderFullWidth ? '' : 'auto'}`,
      maxWidth: isSliderFullWidth
        ? '100vw'
        : `${props?.isBlogListingPage ? BLOG_SECTION_MAX_WIDTH : MAX_SECTION_WIDTH}px`,
      ...(props.overrides?.layout?.styles || {}),
    };
  }

  return (
    <div
      className={classNames(
        'listPresentationContainer',
        props?.isTestimonialListingPage ? 'tw-mx-auto tw-max-w-[1200px]' : ''
      )}
      style={{
        background:
          typeof props.overrides?.layout?.styles?.background === 'string'
            ? props.overrides?.layout?.styles?.background
            : sectionColors?.background,
      }}
    >
      <section
        ref={sectionContainerRefSetter}
        className={classNames(
          `${cardFrameConfig?.contentAlignment !== 'left' ? (!shouldAutoScrollSlider ? 'tw-items-center' : '') : ''} tw-flex tw-w-[100%] tw-flex-col`
        )}
        style={getSectionStyles()}
        onClick={handleHeadingClick}
      >
        {children}
      </section>
    </div>
  );
}

export function LayoutBodyWrapper({ children }) {
  const data = useListPresentationContextData();
  const props = data.props;
  const { cardFrameConfig, contentTypeConditions, listPresentationConfig, isMobile } =
    data.state;
  const { itemsContainerRefSetter } = data.refSetters;
  const { getConfigsAndUtils } = data.utilities;
  const {
    slideOffset,
    widthAdditionals,
    shouldAutoScrollSlider,
    isLayoutCarousel,
    containerTracks,
    viewAllItemsConfig,
    gridGapAxis,
    listVerticalPadding,
    listHorizontalPadding,
    isCardScaleAnimation,
    featuredReelConfigs,
    oneGridCellGeometry,
  } = getConfigsAndUtils().generalValues;
  const { setCarouselIndicatorConfigs } = getConfigsAndUtils().setters;
  const isProductTestimonials = listPresentationConfig?.type === ADMIN_WIDGETS.PRODUCT_TESTIMONIALS.type


  function getCardContainerClass(): string {
    return classNames(
      `no-scrollbar tw-max-w-[100%] tw-overscroll-x-none tw-flex-shrink-0`,
      cardFrameConfig?.contentAlignment !== 'left' && !props?.isBlogListingPage
        ? !shouldAutoScrollSlider
          ? contentTypeConditions.gallery &&
            listPresentationConfig?.layoutName === COMMON_LAYOUT_TYPE.THREE
            ? 'tw-w-[100%]'
            : 'tw-mx-auto'
          : ''
        : '',
      !isLayoutCarousel ? `tw-grid` : `tw-flex tw-items-stretch`,
      listPresentationConfig?.layoutType === 'carousel' &&
        contentTypeConditions.logoSlider &&
        !shouldAutoScrollSlider
        ? `!tw-pb-[15px] !tw-pt-[35px] ${isMobile ? '!tw-px-[16px]' : ''}`
        : ''
    );
  }

  function getCardContainerStyles(): React.CSSProperties {
    const style: React.CSSProperties = {};
    if (!isLayoutCarousel) {
      style.gridTemplateColumns = `repeat(${viewAllItemsConfig?.areAllItemsVisible ? 'auto-fit' : containerTracks?.numColumnsMax}, ${oneGridCellGeometry.width}px)`;
      if (!viewAllItemsConfig?.areAllItemsVisible) {
        style.gridTemplateRows = `repeat(${containerTracks?.numRows}, auto)`;
      }
      for (const key in gridGapAxis) {
        if (key) {
          style[key] = gridGapAxis[key];
        }
      }
    } else {
      style.overflowX = 'auto';
      style.gap = gridGapAxis.columnGap;
    }
    style.overflowY =
      contentTypeConditions.featured && !contentTypeConditions.featuredReels
        ? 'hidden'
        : 'visible';
    style.padding = `${listVerticalPadding}px ${listHorizontalPadding}px`;
    if (isCardScaleAnimation) {
      style.marginBottom = '-20px';
      style.marginTop = '-20px';
    }
    if (featuredReelConfigs.expandFocusedReel) {
      style.paddingLeft = `calc(50% - ${oneGridCellGeometry.width / 2}px)`;
      style.paddingRight = `calc(50% - ${oneGridCellGeometry.width / 2}px)`;
      style.scrollSnapType = 'x mandatory';
    }
    if(isProductTestimonials && isLayoutCarousel) {
      style.padding = '0 20px'
    }
    if (contentTypeConditions.instagram) {
      if(isLayoutCarousel) {
        style.padding = getPaddingForInstaWidget(listPresentationConfig, isMobile)
      }
      else {
        style.padding = 0
        style.margin = 0
      }
    }
    return style;
  }

  /** This renders card columns either as carousel or grid */
  return (
    <div
      className={getCardContainerClass()}
      style={getCardContainerStyles()}
      ref={itemsContainerRefSetter}
      onScroll={(e) =>
        onCarouselScroll({
          e,
          setCarouselIndicatorConfigs,
          slideOffset,
          widthOffset: widthAdditionals,
        })
      }
    >
      {children}
    </div>
  );
}
